import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Avatar,
  IconButton,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import Face1Icon from '@mui/icons-material/Face';
import Face2Icon from '@mui/icons-material/Face2';
import Face3Icon from '@mui/icons-material/Face3';
import Face4Icon from '@mui/icons-material/Face4';
import Face5Icon from '@mui/icons-material/Face5';
import Face6Icon from '@mui/icons-material/Face6';
import SchoolIcon from '@mui/icons-material/School';
import ScienceIcon from '@mui/icons-material/Science';
import CalculateIcon from '@mui/icons-material/Calculate';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';

interface AvatarOption {
  id: string;
  icon: React.ReactNode;
  color: string;
  label: string;
}

interface AvatarSelectorProps {
  open: boolean;
  onClose: () => void;
  currentAvatar: AvatarOption | null;
  onSelectAvatar: (avatar: AvatarOption) => void;
}

const avatarOptions: AvatarOption[] = [
  {
    id: 'default',
    icon: <PersonIcon />,
    color: '#764ba2',
    label: 'Default',
  },
  {
    id: 'face1',
    icon: <Face1Icon />,
    color: '#667eea',
    label: 'Student 1',
  },
  {
    id: 'face2',
    icon: <Face2Icon />,
    color: '#f093fb',
    label: 'Student 2',
  },
  {
    id: 'face3',
    icon: <Face3Icon />,
    color: '#4facfe',
    label: 'Student 3',
  },
  {
    id: 'face4',
    icon: <Face4Icon />,
    color: '#43e97b',
    label: 'Student 4',
  },
  {
    id: 'face5',
    icon: <Face5Icon />,
    color: '#fa709a',
    label: 'Student 5',
  },
  {
    id: 'face6',
    icon: <Face6Icon />,
    color: '#fee140',
    label: 'Student 6',
  },
  {
    id: 'scholar',
    icon: <SchoolIcon />,
    color: '#30cfd0',
    label: 'Scholar',
  },
  {
    id: 'scientist',
    icon: <ScienceIcon />,
    color: '#a8edea',
    label: 'Scientist',
  },
  {
    id: 'mathematician',
    icon: <CalculateIcon />,
    color: '#ff9a9e',
    label: 'Math Genius',
  },
  {
    id: 'reader',
    icon: <MenuBookIcon />,
    color: '#fbc2eb',
    label: 'Book Lover',
  },
  {
    id: 'thinker',
    icon: <PsychologyIcon />,
    color: '#a1c4fd',
    label: 'Thinker',
  },
  {
    id: 'happy',
    icon: <EmojiEmotionsIcon />,
    color: '#ffecd2',
    label: 'Happy',
  },
];

const AvatarSelector: React.FC<AvatarSelectorProps> = ({
  open,
  onClose,
  currentAvatar,
  onSelectAvatar,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarOption | null>(
    currentAvatar
  );

  const handleSelect = (avatar: AvatarOption) => {
    setSelectedAvatar(avatar);
  };

  const handleConfirm = () => {
    if (selectedAvatar) {
      onSelectAvatar(selectedAvatar);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100%' : '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Choose Your Avatar
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Select an avatar that represents you
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#f8f9fa' }}>
        <Grid container spacing={2}>
          {avatarOptions.map((avatar) => (
            <Grid item xs={6} sm={4} md={3} key={avatar.id}>
              <Box
                onClick={() => handleSelect(avatar)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: '#fff',
                  border: '2px solid',
                  borderColor:
                    selectedAvatar?.id === avatar.id
                      ? avatar.color
                      : 'transparent',
                  boxShadow:
                    selectedAvatar?.id === avatar.id
                      ? `0 4px 12px ${avatar.color}40`
                      : '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 6px 16px ${avatar.color}30`,
                    borderColor: avatar.color,
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: avatar.color,
                    width: { xs: 50, sm: 60 },
                    height: { xs: 50, sm: 60 },
                    fontSize: { xs: 24, sm: 30 },
                  }}
                >
                  {avatar.icon}
                </Avatar>
                <Typography
                  variant="caption"
                  fontWeight={selectedAvatar?.id === avatar.id ? 600 : 400}
                  sx={{
                    fontSize: { xs: 11, sm: 12 },
                    textAlign: 'center',
                    color:
                      selectedAvatar?.id === avatar.id ? avatar.color : '#666',
                  }}
                >
                  {avatar.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#5568d3',
                bgcolor: '#f0f2f5',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedAvatar}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#667eea',
              '&:hover': {
                bgcolor: '#5568d3',
              },
              '&:disabled': {
                bgcolor: '#e5e7eb',
              },
            }}
          >
            Confirm
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export { AvatarSelector, avatarOptions };
export type { AvatarOption };